// src/views/pages/Positionreport.js
//
// New UI (cream/gold theme — Image 2) with ALL old UI functionality:
//   ✅ Checkboxes (select rows for bulk close / rollover)
//   ✅ Close ✕ button per row → ClosePositionModal (Lot/Qty/Price + Market/Limit)
//   ✅ Rollover button (bottom-left of table, same as old UI)
//   ✅ Close Position bulk button (header area, disabled when nothing selected)
//   ✅ Confirm-all modal before bulk close
//   ✅ Infinite scroll (same logic as TablePositionReport)
//   ✅ Live socket LTP / MTM updates
//   ✅ Sort by column click
//   ✅ Debounced search
//   ✅ Auto-reload after close/rollover (isClosePosition watcher)
//   ✅ All filters: Day/Net, Outstanding/Closed, Segment, Valan, Report Type,
//      Client, Symbol, Authority, Ex Date
//   ✅ Totals bar: N.B.Q, N.S.Q, T.N.Q, T.B, T.S, T.V, T.H, T.R, L.B, T.MTM

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from 'react-redux';
import Select from 'react-select';
import { selectFieldStyles } from 'Store/styled/select';
import moment from 'moment';
import _ from 'lodash';
import "../../assets/scss/position-report-embedded.scss";
import { FONT_ROBOTO, FONT_ROBOTO_MONO } from "../../utils/fonts";
import { api, setAuthToken } from '../../utils/api';
import {
  confirmRolloverPositions,
  alertSelectPositionFirst,
} from '../../utils/tradingAlerts';
import { getValansList } from '../../Store/Reducer/valan.slice';

import {
  getPositionReportList,
  exportPositionReport,
  closePosition,
  saveAllClosePosition,
  rollOverPosition,
  setPositionReportSearchText,
  setStartPagePositionReport,
  setPositionReportFilterValue,
  setPositionReportList,
  setfilteredPositionReportList,
  setClosePositonSelected,
  setPositionCloseConfirmDialog,
  setPositionCloseData,
  setIsClosePosition,
  setPositionDisplayNames,
  setSocketData,
} from '../../Store/Reducer/Positionreport.slice';
import { setIsAddedOrder } from '../../Store/Reducer/marketwatch.slice';
import getSocket from '../../utils/socketInstance';
import { formatOpenPositionTime, applyLiveQuoteToOpenPosition } from '../../utils/openPositionMapper';
import { useBrokerTimeZoneId } from '../../utils/dateFormats';
import useMobileViewport from "hooks/useMobileViewport";

// ── Session ───────────────────────────────────────────────────────────────────
function getSessionData() {
  try {
    const raw = sessionStorage.getItem('fxbazzar_user')
             || sessionStorage.getItem('fxbazzar_admin')
             || '{}';
    const d = JSON.parse(raw);
    return {
      userId  : d.userId   || d.id       || d.UserId   || '',
      userName: d.userName || d.UserName || d.name     || '',
      userRole: d.role     || d.Role     || (d.roles && d.roles[0]) || 'User',
      roles   : d.roles    || d.Roles    || [d.role || d.Role || 'User'],
      token   : d.token    || d.Token    || '',
    };
  } catch {
    return { userId:'', userName:'', userRole:'User', roles:['User'], token:'' };
  }
}

// ── Design tokens — light (cream/teal) ───────────────────────────────────────
const C_LIGHT = {
  headerBg    : '#1A7A70',
  headerBorder: '#145e57',
  pageBg      : '#f4fbfa',
  panelBg     : '#ffffff',
  panel2Bg    : '#edf7f6',
  border      : '#cfe5e3',
  tx          : '#0f3e3a',
  muted       : '#6b8a87',
  green       : '#1A7A70',
  red         : '#ef5350',
  accent      : '#2f9e93',
  checkBg     : 'rgba(26,122,112,0.08)',
};

const C_DARK = {
  headerBg    : '#1A7A70',
  headerBorder: '#145e57',
  pageBg      : 'var(--bg-primary, #000000)',
  panelBg     : 'var(--bg-panel, #000000)',
  panel2Bg    : 'var(--bg-secondary, #000000)',
  border      : 'var(--border, rgba(255,255,255,0.09))',
  tx          : 'var(--text-primary, #ffffff)',
  muted       : 'var(--text-muted, rgba(255,255,255,0.48))',
  green       : 'var(--green, #26a69a)',
  red         : '#ef5350',
  accent      : '#26a69a',
  checkBg     : 'rgba(38, 166, 154, 0.12)',
};

/** Chart-docked panel: align with Fxbazzar / MarketWatch (beige shell + teal accent) */
const EMB = {
  card: "var(--bg-primary, #f4f0ec)",
  card2: "var(--bg-secondary, #ede8e3)",
  panel: "#fffef9",
  border: "var(--border, #c0b8ae)",
  borderSoft: "#ddd5ca",
  text: "var(--text-primary, #2a1a1a)",
  muted: "var(--text-muted, #7a7168)",
  teal: "var(--green, #1a7a70)",
  tealSoft: "rgba(26, 122, 112, 0.12)",
  btnSecondary: "#8a8276",
};

// ── react-select style factory ────────────────────────────────────────────────
function selSt(colors = C_LIGHT) {
  return {
    ...selectFieldStyles,
    control           : b => ({ ...b, background:colors.panelBg, borderColor:colors.border, minHeight:34, fontSize:13, boxShadow:'none', cursor:'pointer', borderRadius:4 }),
    singleValue       : b => ({ ...selectFieldStyles.singleValue(b), color:colors.tx }),
    input             : b => ({ ...selectFieldStyles.input(b), color:colors.tx }),
    placeholder       : b => ({ ...selectFieldStyles.placeholder(b), color:colors.muted, fontSize:13 }),
    menu              : b => ({ ...b, background:colors.panelBg, border:`1px solid ${colors.border}`, zIndex:9999 }),
    option            : (b,s) => ({ ...b, background:s.isFocused?colors.panel2Bg:colors.panelBg, color:colors.tx, fontSize:13, cursor:'pointer' }),
    clearIndicator    : b => ({ ...b, padding:'2px 4px', cursor:'pointer' }),
    dropdownIndicator : b => ({ ...b, padding:'2px 4px' }),
    indicatorSeparator: () => ({ display:'none' }),
  };
}

/** Tighter react-select for embedded panel below chart */
function selStCompact(colors = C_LIGHT) {
  return {
    ...selectFieldStyles,
    control           : b => ({ ...b, background:colors.panelBg, borderColor:colors.border, minHeight:30, fontSize:11, boxShadow:'none', cursor:'pointer', borderRadius:6 }),
    singleValue       : b => ({ ...selectFieldStyles.singleValue(b), color:colors.tx, fontSize:11 }),
    input             : b => ({ ...selectFieldStyles.input(b), color:colors.tx, fontSize:11 }),
    placeholder       : b => ({ ...selectFieldStyles.placeholder(b), color:colors.muted, fontSize:11 }),
    menu              : b => ({ ...b, background:colors.panelBg, border:`1px solid ${colors.border}`, zIndex:9999, fontSize:11 }),
    option            : (b,s) => ({ ...b, background:s.isFocused?colors.panel2Bg:colors.panelBg, color:colors.tx, fontSize:11, cursor:'pointer', padding:'6px 8px' }),
    clearIndicator    : b => ({ ...b, padding:'2px', cursor:'pointer' }),
    dropdownIndicator : b => ({ ...b, padding:'2px' }),
    indicatorSeparator: () => ({ display:'none' }),
  };
}

/** react-select for embedded dock — matches trading shell chrome */
function selStEmbeddedShell() {
  return {
    ...selectFieldStyles,
    control: (b) => ({
      ...b,
      background: EMB.panel,
      borderColor: EMB.border,
      minHeight: 30,
      fontSize: 11,
      boxShadow: "none",
      cursor: "pointer",
      borderRadius: 8,
    }),
    singleValue: (b) => ({ ...selectFieldStyles.singleValue(b), color: EMB.text, fontSize: 11 }),
    input: (b) => ({ ...selectFieldStyles.input(b), color: EMB.text, fontSize: 11 }),
    placeholder: (b) => ({ ...selectFieldStyles.placeholder(b), color: EMB.muted, fontSize: 11 }),
    menu: (b) => ({ ...b, background: EMB.panel, border: `1px solid ${EMB.border}`, zIndex: 9999, fontSize: 11 }),
    option: (b, s) => ({
      ...b,
      background: s.isFocused ? EMB.card2 : EMB.panel,
      color: EMB.text,
      fontSize: 11,
      cursor: "pointer",
      padding: "6px 8px",
    }),
    clearIndicator: (b) => ({ ...b, padding: "2px", cursor: "pointer" }),
    dropdownIndicator: (b) => ({ ...b, padding: "2px" }),
    indicatorSeparator: () => ({ display: "none" }),
  };
}

const inpSt = (colors = C_LIGHT) => ({
  background  : colors.panelBg,
  border      : `1px solid ${colors.border}`,
  color       : colors.tx,
  padding     : '6px 10px',
  borderRadius: 4,
  fontSize    : 13,
  outline     : 'none',
  width       : '100%',
  boxSizing   : 'border-box',
  fontFamily  : 'inherit',
});

/** Table header — same family as Market Watch / index strip: light bar, dark text */
const thSt = () => ({
  padding      : "9px 12px",
  fontSize     : 11,
  fontWeight   : 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color        : "var(--text-primary, #3d3630)",
  background   : "var(--bg-header, #ddd6cc)",
  whiteSpace   : "nowrap",
  position     : "sticky",
  top          : 0,
  zIndex       : 2,
  borderBottom : "1px solid var(--border, #c0b8ae)",
  borderRight  : "1px solid var(--border-light, #e8e4df)",
  cursor       : "pointer",
  userSelect   : "none",
  textAlign    : "left",
  boxShadow    : "inset 0 -1px 0 rgba(42, 26, 26, 0.06)",
});

const tdSt = (tint) => ({
  padding     : "8px 12px",
  whiteSpace  : "nowrap",
  fontSize    : 13,
  color       : "var(--text-primary, #0f3e3a)",
  background  :
    tint === "buy"
      ? "color-mix(in srgb, var(--green, #1a7a70) 8%, var(--bg-primary, #fff) 92%)"
      : tint === "sell"
        ? "color-mix(in srgb, #ef5350 7%, var(--bg-primary, #fff) 93%)"
        : "var(--bg-primary, #ffffff)",
  borderBottom: "1px solid var(--border-light, #e8e4df)",
  borderRight : "1px solid var(--border-light, #e8e4df)",
});

const btnSt = (bg, disabled) => ({
  padding     : '7px 20px',
  background  : disabled ? '#ccc' : bg,
  color       : '#fff',
  border      : 'none',
  borderRadius: 4,
  cursor      : disabled ? 'not-allowed' : 'pointer',
  fontSize    : 13,
  fontWeight  : 600,
  whiteSpace  : 'nowrap',
  opacity     : disabled ? 0.75 : 1,
});

const btnStCompact = (bg, disabled) => ({
  ...btnSt(bg, disabled),
  padding: '5px 12px',
  fontSize: 11,
  borderRadius: 6,
});

// ── Options ───────────────────────────────────────────────────────────────────
const CR_TYPES   = [{ value:'day', label:'Day Wise' }, { value:'net', label:'Net Wise' }];
const DISP_TYPES = [{ value:'outstanding', label:'Outstanding' }, { value:'all', label:'Closed' }];
const REPORT_TYPES = [
  { value:'client', label:'Client Wise' },
  { value:'script', label:'Script Wise' },
  { value:'self',   label:'Self Wise'   },
];

/** Full-viewport overlay — must portal to body (embedded position panel clips fixed children). */
function ClosePositionPortal({ children, onBackdropClick, loading, darkTheme }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (typeof document === "undefined") return null;

  const themeClass =
    darkTheme || document.querySelector(".trading-platform.dark") ? "pr-close-pop--dark" : "pr-close-pop--light";

  return createPortal(
    <div className={`pr-close-pop ${themeClass}`} role="presentation">
      <div
        className="pr-close-pop__backdrop"
        aria-hidden
        onClick={loading ? undefined : onBackdropClick}
      />
      <div
        className="pr-close-pop__card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CLOSE POSITION MODAL (mirrors old ClosePositionModal from TablePositionReport)
// ─────────────────────────────────────────────────────────────────────────────
function ClosePositionModal({ row, loading, onClose, onConfirm, darkTheme = false }) {
  /** Inline confirm (fxbazzar_frontend used Swal here — second Swal over this modal hangs on mobile). */
  const [phase, setPhase] = useState("edit");
  const [marketType, setMarketType] = useState('market');
  const [lot,   setLot]   = useState(Math.abs(row.netLot  || 0));
  const [qty,   setQty]   = useState(Math.abs(row.netQty  || 0));
  const [price, setPrice] = useState(row.ltp || 0);
  const [errors, setErrors] = useState({});
  const oneQtySize = row.netLot ? Math.abs(row.netQty / row.netLot) : 1;

  useEffect(() => {
    setPhase("edit");
    setMarketType("market");
    setLot(Math.abs(row.netLot || 0));
    setQty(Math.abs(row.netQty || 0));
    setPrice(row.ltp || 0);
    setErrors({});
  }, [row.rowId]);

  const validate = () => {
    const e = {};
    if (!lot  || lot  <= 0)                          e.lot   = `Lot required`;
    if (!qty  || qty  <= 0)                          e.qty   = `Qty required`;
    if (!price || price <= 0)                        e.price = `Price required`;
    if (Math.abs(lot) > Math.abs(row.netLot || 0))   e.lot   = `Max: ${Math.abs(row.netLot)}`;
    if (Math.abs(qty) > Math.abs(row.netQty || 0))   e.qty   = `Max: ${Math.abs(row.netQty)}`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const summaryPayload = () => ({ lot, qty, price, marketType, oneQtySize });

  return (
    <ClosePositionPortal onBackdropClick={onClose} loading={loading} darkTheme={darkTheme}>
        <div className="pr-close-pop__head">
          <span className="pr-close-pop__title">
            {phase === "confirm" ? "Confirm close" : "✕ Close Position"}
          </span>
          <button type="button" className="pr-close-pop__close" disabled={loading} onClick={onClose} aria-label="Close">✕</button>
        </div>

        {phase === "edit" ? (
          <>
        <div className="pr-close-pop__body">
          <div className="pr-close-pop__meta">
            <span><span className="pr-close-pop__meta-label">Client: </span><strong>{row.clientName}</strong></span>
            <span><span className="pr-close-pop__meta-label">Script: </span><strong>{row.script}</strong></span>
          </div>

          <div className="pr-close-pop__radios">
            {['market','limit'].map(t => (
              <label key={t} className="pr-close-pop__radio">
                <input type="radio" checked={marketType===t}
                  onChange={() => { setMarketType(t); if (t==='market') setPrice(row.ltp); }} />
                {t === 'market' ? 'Market' : 'Limit / BuyStop'}
              </label>
            ))}
          </div>

          <div className="pr-close-pop__row2">
            <div className="pr-close-pop__field">
              <div className="pr-close-pop__label">LOT *</div>
              <input type="number" className="pr-close-pop__input" min={0} max={Math.abs(row.netLot||0)} value={lot}
                onChange={e => {
                  const v = +e.target.value;
                  setLot(v);
                  setQty(Math.round(Math.abs(v * oneQtySize)));
                }} />
              {errors.lot && <div className="pr-close-pop__err">{errors.lot}</div>}
            </div>
            <div className="pr-close-pop__field">
              <div className="pr-close-pop__label">QTY *</div>
              <input type="number" className="pr-close-pop__input" min={1} max={Math.abs(row.netQty||0)} value={qty}
                disabled={row.segmentId===2 || row.segmentId===3}
                onChange={e => setQty(+e.target.value)} />
              {errors.qty && <div className="pr-close-pop__err">{errors.qty}</div>}
            </div>
          </div>

          <div className="pr-close-pop__field">
            <div className="pr-close-pop__label">PRICE *</div>
            <input type="number" className="pr-close-pop__input" min={0} value={price}
              disabled={marketType==='market'}
              onChange={e => setPrice(+e.target.value)} />
            {errors.price && <div className="pr-close-pop__err">{errors.price}</div>}
          </div>
        </div>
        <div className="pr-close-pop__footer">
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--danger" disabled={loading}
            onClick={() => { if (validate()) setPhase("confirm"); }}>
            Close Position
          </button>
        </div>
          </>
        ) : (
          <>
            <div className="pr-close-pop__body">
              <div className="pr-close-pop__confirm-title">Are you sure?</div>
              <div className="pr-close-pop__confirm-text">
                Lot: <strong>{lot}</strong>, Qty: <strong>{qty}</strong>, Price: <strong>{price}</strong>
              </div>
            </div>
            <div className="pr-close-pop__footer">
              <button type="button" className="pr-close-pop__btn pr-close-pop__btn--outline-danger" disabled={loading}
                onClick={() => setPhase("edit")}>
                Cancel
              </button>
              <button type="button" className="pr-close-pop__btn pr-close-pop__btn--confirm" disabled={loading}
                onClick={() => onConfirm(summaryPayload())}>
                {loading ? "Closing…" : "Yes, Close it!"}
              </button>
            </div>
          </>
        )}
    </ClosePositionPortal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIRM ALL CLOSE MODAL
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmAllModal({ count, loading, onClose, onConfirm, darkTheme = false }) {
  return (
    <ClosePositionPortal onBackdropClick={onClose} loading={loading} darkTheme={darkTheme}>
        <div className="pr-close-pop__head">
          <span className="pr-close-pop__title">⚠ Close All Positions</span>
          <button type="button" className="pr-close-pop__close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="pr-close-pop__body pr-close-pop__body--center">
          <div className="pr-close-pop__confirm-text">
            Close <strong className="pr-close-pop__danger-text">{count}</strong> selected position{count!==1?'s':''}?
          </div>
          <div className="pr-close-pop__hint">This action cannot be undone.</div>
        </div>
        <div className="pr-close-pop__footer">
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="pr-close-pop__btn pr-close-pop__btn--danger" disabled={loading} onClick={onConfirm}>
            {loading ? 'Processing…' : 'OK, Close All'}
          </button>
        </div>
    </ClosePositionPortal>
  );
}

function PositionAdvRow({
  row,
  isCheckBoxVisible,
  isCloseButtonVisible,
  isAdminRole,
  onCheck,
  onClose,
  orderTypeForRow,
  C,
}) {
  const mtmVal = (row.profit ?? row.mtm ?? 0) * (row.currencyRate || 1);
  const mtmColor = mtmVal > 0 ? C.green : mtmVal < 0 ? C.red : C.tx;
  const mtmTone = mtmVal > 0 ? "up" : mtmVal < 0 ? "down" : "flat";
  const side = orderTypeForRow(row);
  const priceDp = row.digitAfterPoint ?? 2;
  const ltpLabel = "Price";

  const columns = [
    { key: "type", label: "Type", value: row.type || side, color: mtmColor },
    { key: "lots", label: "Lots", value: row.lots },
    { key: "qty", label: "Qty", value: row.qty },
    { key: "open", label: "Price", value: Number(row.openPrice || 0).toFixed(priceDp) },
    { key: "ltp", label: ltpLabel, value: Number(row.currentPrice ?? row.ltp ?? 0).toFixed(priceDp) },
    { key: "pnl", label: "P&L", value: mtmVal.toFixed(2), color: mtmColor },
    { key: "time", label: "Time", value: formatOpenPositionTime(row.openTime) },
  ];

  return (
    <article
      className={`pr-adv-row pr-adv-row--${side.toLowerCase()}${row.isCheck ? " pr-adv-row--selected" : ""}`}
    >
      <div className="pr-adv-row__head">
        {isCheckBoxVisible && row.netQty !== 0 && (
          <input
            type="checkbox"
            className="pr-adv-row__check"
            checked={!!row.isCheck}
            onChange={() => onCheck(row)}
            aria-label={`Select ${row.script}`}
          />
        )}

        <div className="pr-adv-row__identity">
          <div className="pr-adv-row__title">
            <span className="pr-adv-row__symbol">{row.script}</span>
            <span className={`pr-adv-row__side pr-adv-row__side--${side.toLowerCase()}`}>{side}</span>
          </div>
          <div className="pr-adv-row__meta">
            <span className="pr-adv-row__seg">{row.segment}</span>
            {isAdminRole && row.authorityName && (
              <>
                <span className="pr-adv-row__dot" aria-hidden>·</span>
                <span className="pr-adv-row__client">{row.authorityName}</span>
              </>
            )}
          </div>
        </div>

        <div className="pr-adv-row__actions">
          <div className={`pr-adv-row__mtm pr-adv-row__mtm--${mtmTone}`}>
            <strong style={{ color: mtmColor }}>
              {mtmVal >= 0 ? "+" : ""}
              {mtmVal.toFixed(2)}
            </strong>
            <span className="pr-adv-row__mtm-lbl">MTM</span>
            {(row.currencyRate || 1) > 1 && (
              <span className="pr-adv-row__mtm-fx">
                {row.currencySymbol}:{row.currencyRate}
              </span>
            )}
          </div>
          {isCloseButtonVisible && row.netQty !== 0 && (
            <button
              type="button"
              className="pr-adv-row__close"
              title="Close Position"
              onClick={() => onClose(row)}
              aria-label="Close position"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="pr-adv-row__table-wrap">
        <div className="pr-adv-row__table">
          {columns.map((col) => (
            <div key={col.key} className="pr-adv-row__cell">
              <span className="pr-adv-row__cell-l">{col.label}</span>
              <span
                className="pr-adv-row__cell-v"
                style={col.color ? { color: col.color } : undefined}
              >
                {col.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function PositionReport({ embedded = false, darkTheme = false } = {}) {
  const dispatch = useDispatch();
  const isMobileViewport = useMobileViewport();
  const showMobileCards = !embedded && isMobileViewport;
  useBrokerTimeZoneId();

  // ── Redux ─────────────────────────────────────────────────────────────────
  const {
    filteredPositionReportList = [],
    positionReportList = [],
    duplicatePositionReportList = [],
    positionReportLoading = false,
    totalRecordPositionReport = 0,
    currentPagePositionReport = 1,
    pageSizePositionReport = 10000,
    positionReportFilterValue = {},
    positionReportSearchText = "",
    closePositonSelected = [],
    isPositionCloseLoading = false,
    positionCloseConfirmDialog = false,
    positionCloseData = null,
    isClosePosition = false,
    liveRateFromSocket = null,
    positionDisplayNames = [],
  } = useSelector(({ positionReport }) => positionReport || {});

  const { isAddedOrder = false } = useSelector(({ marketWatch }) => marketWatch || {});

  const { valansList = [] } = useSelector(({ valan }) => valan || {});

  // ── Session ───────────────────────────────────────────────────────────────
  const { userId, userName, userRole, token } = getSessionData();
  const masterRoles = ['Super Admin','Admin','Super Master','Master'];
  const isMasterRole = masterRoles.includes(userRole);
  const isPartner    = userRole === 'Sub Broker' || userRole === 'Partner';
  const isAdminRole  = masterRoles.includes(userRole);

  // ── Auth token ────────────────────────────────────────────────────────────
  useEffect(() => { if (token) setAuthToken(token); }, []);

  // ── Dropdown data ─────────────────────────────────────────────────────────
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [valanOptions,   setValanOptions]   = useState([]);
  const [symbolOptions,  setSymbolOptions]  = useState([]);
  const [clientOptions,  setClientOptions]  = useState([]);
  const [loadingSeg,     setLoadingSeg]     = useState(false);
  const [loadingCli,     setLoadingCli]     = useState(false);

  // ── Filter state ──────────────────────────────────────────────────────────
  /** Full page + chart-docked: legacy defaults — Net Wise + Outstanding + auto-fetch (embedded effect locks disptype). */
  const [filters, setFilters] = useState(() => ({
    crtype     : "net",
    disptype   : "outstanding",
    reporttype : "client",
    segment    : null,
    valan      : null,
    clientcode : null,
    symbol     : null,
    subbroker  : null,
    exdate     : new Date(),
    isExDateSel: false,
  }));
  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const positionReportSearchTextRef = useRef(positionReportSearchText);
  positionReportSearchTextRef.current = positionReportSearchText;
  const fullPageAutoFetchedRef = useRef(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  /** Docked panel: first row = all segments (API segmentId null). Full page uses API list only. */
  const embeddedSegmentOptions = useMemo(
    () => [{ label: "All segments", value: null }, ...segmentOptions],
    [segmentOptions]
  );

  // Match react-select option references after remount / reload (avoids blank Segment/Valan labels)
  const segmentSelectValue = useMemo(() => {
    if (!filters.segment) return null;
    const pool = embedded ? embeddedSegmentOptions : segmentOptions;
    return pool.find((o) => o.value === filters.segment.value) ?? filters.segment;
  }, [filters.segment, segmentOptions, embedded, embeddedSegmentOptions]);

  const valanSelectValue = useMemo(() => {
    if (!filters.valan) return null;
    const fv = filters.valan.value;
    const fid = fv != null && typeof fv === "object" && "id" in fv ? fv.id : fv;
    const found = valanOptions.find((o) => {
      const ov = o.value;
      const oid = ov != null && typeof ov === "object" && "id" in ov ? ov.id : ov;
      return fid != null && oid != null && String(fid) === String(oid);
    });
    return found ?? filters.valan;
  }, [filters.valan, valanOptions]);

  // Chart-docked: always Net Wise + Outstanding (no UI toggles); segment filter only
  useEffect(() => {
    if (!embedded) return;
    setFilters((f) => ({
      ...f,
      crtype: "net",
      disptype: "outstanding",
    }));
  }, [embedded]);

  // ── Sort / UI state ───────────────────────────────────────────────────────
  const [sortObj,       setSortObj]       = useState(null);
  const [showConfirmAll,setShowConfirmAll] = useState(false);
  const [searchInput,   setSearchInput]   = useState('');

  // Embedded: no search UI — keep server search empty
  useEffect(() => {
    if (!embedded) return;
    dispatch(setPositionReportSearchText(""));
    setSearchInput("");
    positionReportSearchTextRef.current = "";
  }, [embedded, dispatch]);

  // ── Load segments + clients on mount ─────────────────────────────────────
  useEffect(() => {
    dispatch(getValansList());

    setLoadingSeg(true);
    api.get('master/segment/get-segments')
      .then(({ data }) => setSegmentOptions(
        (data?.data || []).map(s => ({ label:s.name, value:s.id, digitAfterPoint:s.digitAfterPoint }))
      ))
      .catch(() => {})
      .finally(() => setLoadingSeg(false));

    if (userRole !== 'User') {
      setLoadingCli(true);
      api.get('common/get-account-names-by-rolename/User')
        .then(({ data }) => setClientOptions(
          (data?.data || []).map(c => ({ value:c.value??c.id, label:c.label??c.name }))
        ))
        .catch(() => {})
        .finally(() => setLoadingCli(false));
    }

    // Do not clear position lists / totals here: full page and chart-docked instances
    // share Redux; unmounting one route would wipe data for the other.
    return () => {
      dispatch(setClosePositonSelected([]));
      dispatch(setPositionDisplayNames([]));
    };
  }, []);

  // ── Build valan options when valansList arrives ───────────────────────────
  useEffect(() => {
    if (!valansList || valansList.length === 0) return;
    const opts = valansList
      .filter(v => v.status == 1)
      .map(v => ({ label:v.name, value:v }));
    setValanOptions(opts);
    if (opts.length === 0) return;
    setFilters((f) => (f.valan ? f : { ...f, valan: opts[0] }));
  }, [valansList]);

  // ── Load symbols when segment changes ────────────────────────────────────
  useEffect(() => {
    if (!filters.segment) { setSymbolOptions([]); return; }
    api.get(`market-watch/get-symbols-by-segment/${filters.segment.value}`)
      .then(({ data }) => setSymbolOptions(
        (data?.data || []).map(s => ({ value:s.value??s.id, label:s.label??s.name }))
      ))
      .catch(() => setSymbolOptions([]));
  }, [filters.segment]);

  // ── Auto-reload after close / rollover (isClosePosition watcher) ─────────
  useEffect(() => {
    if (!isClosePosition) return;
    dispatch(getPositionReportList({
      ...positionReportFilterValue,
      searchText : positionReportSearchText,
      pageNo     : 1,
      pageSize   : pageSizePositionReport || 10000,
      appendData : false,
    }));
    dispatch(setIsClosePosition(false));
  }, [
    isClosePosition,
    dispatch,
    positionReportFilterValue,
    positionReportSearchText,
    pageSizePositionReport,
  ]);

  // ── Socket: subscribe + live P&L ─────────────────────────────────────────
  useEffect(() => {
    const socket = getSocket();
    const onMessage = (data) => {
      if (data && Object.keys(data).length > 0) {
        dispatch(setSocketData(data));
      }
    };
    socket.on("message", onMessage);
    return () => {
      socket.off("message", onMessage);
    };
  }, [dispatch]);

  useEffect(() => {
    if (userRole !== "User") return undefined;
    const names = positionDisplayNames;
    if (!names?.length) return undefined;
    const socket = getSocket();
    const currentSymbols = [...new Set(names)].filter(Boolean);
    if (socket?.connected && currentSymbols.length) {
      socket.emit("room", currentSymbols.toString());
    }
    return () => {
      if (socket?.connected && currentSymbols.length) {
        socket.emit("leave_room", currentSymbols.toString());
      }
    };
  }, [userRole, positionDisplayNames]);

  useEffect(() => {
    const tick = liveRateFromSocket || {};
    if (!tick.Script || !filteredPositionReportList?.length) return;

    const updated = filteredPositionReportList.map((row) =>
      applyLiveQuoteToOpenPosition(row, tick)
    );
    dispatch(setfilteredPositionReportList(updated));
    dispatch(setPositionReportList(updated));
  }, [liveRateFromSocket]);

  // ── Sort — sync duplicateList → filteredList ─────────────────────────────
  useEffect(() => {
    if (duplicatePositionReportList?.length > 0) {
      let list = [...duplicatePositionReportList];
      if (sortObj) list = _.orderBy(list, [sortObj.id], [sortObj.desc?'desc':'asc']);
      dispatch(setfilteredPositionReportList(list));
    }
  }, [duplicatePositionReportList]);

  // ── GET POSITION ──────────────────────────────────────────────────────────
  const handleGetPosition = useCallback(() => {
    const f = filtersRef.current;
    const payload = {
      ...f,
      userId,
      searchText : '',
      pageNo     : 1,
      pageSize   : pageSizePositionReport || 10000,
      appendData : false,
    };
    dispatch(setPositionReportFilterValue(payload));
    dispatch(setStartPagePositionReport(1));
    dispatch(setPositionReportSearchText(''));
    dispatch(setfilteredPositionReportList([]));
    dispatch(getPositionReportList(payload));
    setSearchInput('');
    dispatch(setClosePositonSelected([]));
  }, [userId, pageSizePositionReport, dispatch]);

  /** Chart-docked: auto-refresh without clearing the table first (less flicker while loading). */
  const fetchEmbeddedPosition = useCallback(() => {
    const f = filtersRef.current;
    if (!f.valan) return;
    const payload = {
      ...f,
      userId,
      searchText: positionReportSearchTextRef.current,
      pageNo: 1,
      pageSize: pageSizePositionReport || 10000,
      appendData: false,
    };
    dispatch(setPositionReportFilterValue(payload));
    dispatch(setStartPagePositionReport(1));
    dispatch(getPositionReportList(payload));
  }, [userId, pageSizePositionReport, dispatch]);

  const embeddedFetchKey = useMemo(() => {
    if (!embedded || !filters.valan) return "";
    const v = filters.valan.value;
    const valanPart = v != null && typeof v === "object" && "id" in v ? v.id : v;
    const segPart =
      filters.segment != null && filters.segment.value != null && filters.segment.value !== ""
        ? String(filters.segment.value)
        : "ALL";
    return [segPart, valanPart].join("\u0001");
  }, [embedded, filters.segment, filters.valan]);

  // Embedded: refetch when filters change (no Get button). segment omitted / cleared = all segments.
  useEffect(() => {
    if (!embeddedFetchKey || loadingSeg) return;
    const f = filtersRef.current;
    if (!f.valan) return;
    dispatch(setClosePositonSelected([]));
    dispatch(setPositionReportSearchText(""));
    setSearchInput("");
    positionReportSearchTextRef.current = "";
    fetchEmbeddedPosition();
  }, [embeddedFetchKey, fetchEmbeddedPosition, loadingSeg, dispatch]);

  const fetchEmbeddedRef = useRef(fetchEmbeddedPosition);
  fetchEmbeddedRef.current = fetchEmbeddedPosition;

  // ── Auto-reload after market trade fill (isAddedOrder watcher) ───────────
  useEffect(() => {
    if (!isAddedOrder) return;
    if (embedded) {
      fetchEmbeddedRef.current?.();
    } else if (positionReportFilterValue && Object.keys(positionReportFilterValue).length > 0) {
      dispatch(getPositionReportList({
        ...positionReportFilterValue,
        searchText : positionReportSearchText,
        pageNo     : 1,
        pageSize   : pageSizePositionReport || 10000,
        appendData : false,
      }));
    }
    dispatch(setIsAddedOrder(false));
  }, [
    isAddedOrder,
    embedded,
    dispatch,
    positionReportFilterValue,
    positionReportSearchText,
    pageSizePositionReport,
  ]);

  /** Docked panel: periodic + tab-focus refresh so new trades show without manual reload */
  useEffect(() => {
    if (!embedded) return undefined;
    const tick = () => {
      const f = filtersRef.current;
      if (!f.valan) return;
      fetchEmbeddedRef.current();
    };
    const id = window.setInterval(tick, 5000);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [embedded]);

  // Full page: first open — auto "Get Position" once valan is ready (same as fxbazzar_frontend GetPositionReportForm).
  useEffect(() => {
    if (embedded) return;
    if (loadingSeg || valanOptions.length === 0) return;
    const f = filtersRef.current;
    if (!f.valan) return;
    if (fullPageAutoFetchedRef.current) return;
    fullPageAutoFetchedRef.current = true;

    const payload = {
      ...f,
      userId,
      searchText: "",
      pageNo: 1,
      pageSize: pageSizePositionReport || 10000,
      appendData: false,
    };
    dispatch(setPositionReportFilterValue(payload));
    dispatch(setStartPagePositionReport(1));
    dispatch(setPositionReportSearchText(""));
    dispatch(setfilteredPositionReportList([]));
    dispatch(getPositionReportList(payload));
    setSearchInput("");
    dispatch(setClosePositonSelected([]));
  }, [embedded, loadingSeg, valanOptions.length, userId, pageSizePositionReport, dispatch]);

  // ── EXPORT ────────────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (!filteredPositionReportList?.length) return;
    dispatch(exportPositionReport({
      ...positionReportFilterValue,
      pageNo     : 1,
      pageSize   : pageSizePositionReport || 10000,
      searchText : positionReportSearchText,
    }));
  }, [positionReportFilterValue, pageSizePositionReport, positionReportSearchText, filteredPositionReportList, dispatch]);

  // ── DEBOUNCED SEARCH ──────────────────────────────────────────────────────
  const debouncedSearch = useCallback(
    _.debounce((value) => {
      dispatch(setPositionReportSearchText(value));
      dispatch(setStartPagePositionReport(1));
      dispatch(getPositionReportList({
        ...positionReportFilterValue,
        searchText : value,
        pageNo     : 1,
        pageSize   : pageSizePositionReport || 10000,
        appendData : false,
      }));
    }, 800),
    [positionReportFilterValue, pageSizePositionReport]
  );

  // ── SORT ──────────────────────────────────────────────────────────────────
  const handleSort = useCallback((key) => {
    const sortKey = key === "orderType" ? "netQty" : key;
    let ns = { id:sortKey, desc:false };
    if (sortObj?.id === sortKey) {
      if (!sortObj.desc) ns = { id:sortKey, desc:true };
      else ns = null;
    }
    setSortObj(ns);
    let list = [...(filteredPositionReportList||[])];
    if (ns) list = _.orderBy(list, [ns.id], [ns.desc?'desc':'asc']);
    else    list = [...(duplicatePositionReportList||[])];
    dispatch(setfilteredPositionReportList(list));
  }, [sortObj, filteredPositionReportList, duplicatePositionReportList, dispatch]);

  // ── CHECKBOX single row ───────────────────────────────────────────────────
  const handleCheck = useCallback((row) => {
    const updated     = (filteredPositionReportList||[]).map(r => r.rowId===row.rowId ? { ...r, isCheck:!r.isCheck } : r);
    const updatedMain = (positionReportList||[]).map(r => r.rowId===row.rowId ? { ...r, isCheck:!r.isCheck } : r);
    dispatch(setfilteredPositionReportList(updated));
    dispatch(setPositionReportList(updatedMain));
    dispatch(setClosePositonSelected(updated.filter(r => r.isCheck && r.netQty !== 0)));
  }, [filteredPositionReportList, positionReportList, dispatch]);

  // ── CHECKBOX select all ───────────────────────────────────────────────────
  const handleCheckAll = useCallback((checked) => {
    const updated     = (filteredPositionReportList||[]).map(r => r.netQty!==0 ? { ...r, isCheck:checked } : r);
    const updatedMain = (positionReportList||[]).map(r => r.netQty!==0 ? { ...r, isCheck:checked } : r);
    dispatch(setfilteredPositionReportList(updated));
    dispatch(setPositionReportList(updatedMain));
    dispatch(setClosePositonSelected(checked ? updated.filter(r => r.isCheck && r.netQty!==0) : []));
  }, [filteredPositionReportList, positionReportList, dispatch]);

  // ── SINGLE CLOSE (confirm step is inside ClosePositionModal — avoids SweetAlert over modal hang) ──
  const handleSingleClose = useCallback(async (formVals) => {
    const act = await dispatch(closePosition({
      ...positionCloseData,
      ...formVals,
      oldNetQty : positionCloseData.netQty,
    }));
    if (act.meta.requestStatus === "fulfilled") {
      dispatch(setPositionCloseConfirmDialog(false));
    }
  }, [positionCloseData, dispatch]);

  // ── BULK CLOSE ────────────────────────────────────────────────────────────
  const handleCloseAll = useCallback(async () => {
    await dispatch(saveAllClosePosition(closePositonSelected));
    dispatch(setClosePositonSelected([]));
    setShowConfirmAll(false);
  }, [closePositonSelected, dispatch]);

  // ── ROLLOVER ──────────────────────────────────────────────────────────────
  const handleRollover = useCallback(async () => {
    if (!closePositonSelected || closePositonSelected.length === 0) {
      await alertSelectPositionFirst();
      return;
    }
    const result = await confirmRolloverPositions(closePositonSelected.length);
    if (!result.isConfirmed) return;
    dispatch(rollOverPosition(closePositonSelected));
  }, [closePositonSelected, dispatch]);

  // ── INFINITE SCROLL ───────────────────────────────────────────────────────
  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop > el.clientHeight + 100) return;
    const loaded = filteredPositionReportList?.length || 0;
    if (loaded >= totalRecordPositionReport || positionReportLoading) return;
    const next = currentPagePositionReport + 1;
    dispatch(setStartPagePositionReport(next));
    dispatch(getPositionReportList({
      ...positionReportFilterValue,
      pageNo     : next,
      pageSize   : pageSizePositionReport || 10000,
      searchText : positionReportSearchText,
      appendData : true,
    }));
  }, [filteredPositionReportList, totalRecordPositionReport, positionReportLoading,
      currentPagePositionReport, positionReportFilterValue, pageSizePositionReport,
      positionReportSearchText, dispatch]);

  // ── TOTALS ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    const data = filteredPositionReportList || [];
    if (!data.length) return null;
    const tBuyQty  = Math.floor(data.reduce((s,r) => s + (r.netQty>0 ? r.netQty  : 0), 0));
    const tSellQty = Math.floor(data.reduce((s,r) => s + (r.netQty<0 ? r.netQty  : 0), 0));
    const tNetQty  = Math.floor(data.reduce((s,r) => s + r.netQty, 0));
    const tBuy     = Math.floor(data.reduce((s,r) => s + (r.netQty>0 ? r.netQty*r.ltp : 0), 0));
    const tSell    = Math.floor(data.reduce((s,r) => s + (r.netQty<0 ? r.netQty*r.ltp : 0), 0));
    const tValue   = tBuy + tSell;
    const tHodiyu  = tBuy + Math.abs(tSell);
    const tMTM     = data.reduce((s,r) => s + ((r.profit ?? r.mtm ?? 0) * (r.currencyRate||1)), 0);
    const ledgerBal= data[0]?.ledgerBalance ?? 0;
    const digit    = Number(data[0]?.digitAfterPoint);
    const priceDp  = Number.isFinite(digit) && digit >= 0 ? digit : 2;

    let sumBuyQty = 0;
    let sumSellQty = 0;
    let sumLots = 0;
    for (const r of data) {
      const isBuy =
        String(r.type || "").toLowerCase() === "buy" || Number(r.netQty) > 0;
      const q = Number(r.qty) || Math.abs(Number(r.netQty) || 0);
      const l = Number(r.lots) || 0;
      sumLots += l;
      if (isBuy) sumBuyQty += q;
      else sumSellQty += q;
    }

    return {
      tBuyQty,
      tSellQty,
      tNetQty,
      tBuy,
      tSell,
      tValue,
      tHodiyu,
      tMTM,
      tRecords: data.length,
      ledgerBal,
      priceDp,
      colFoot: {
        sumBuyQty,
        sumSellQty,
        sumLots,
      },
    };
  }, [filteredPositionReportList]);

  // ── Visibility flags ─────────────────────────────────────────────────────
  // Use current filter selection (filters.crtype) — don't wait for post-submit value.
  // positionReportFilterValue starts as {} so its crtype is always undefined until
  // "Get Position" is clicked — that caused Close/Rollover to never show.
  const isCloseButtonVisible = useMemo(() => {
    if (isPartner) return false;
    if (filters.reporttype === 'self') return false;
    if (filters.crtype === 'day' && userRole === 'Super Admin') return true;
    if (filters.crtype === 'net') return true;
    return false;
  }, [filters.crtype, filters.reporttype, isPartner, userRole]);

  const isCheckBoxVisible = isCloseButtonVisible;

  const isRolloverVisible = useMemo(() => {
    if (isPartner) return false;
    if (filters.reporttype === 'self') return false;
    if (filters.crtype === 'day' && userRole === 'Super Admin') return true;
    if (filters.crtype === 'net') return true;
    return false;
  }, [filters.crtype, filters.reporttype, isPartner, userRole]);

  const allChecked = useMemo(() => {
    const checkable = (filteredPositionReportList||[]).filter(r => r.netQty !== 0);
    return checkable.length > 0 && checkable.every(r => r.isCheck);
  }, [filteredPositionReportList]);

  const showBalance = positionReportFilterValue?.clientcode || userRole === 'User';

  const prTableColCount =
    1 + 9 + (isAdminRole ? 1 : 0);

  const arrow = (id) => {
    return sortObj?.id === id ? (sortObj.desc ? " ↓" : " ↑") : "";
  };

  const orderTypeForRow = (row) => {
    const direct = String(row?.orderType ?? row?.buySell ?? row?.side ?? "").trim().toUpperCase();
    if (direct === "BUY" || direct === "SELL") return direct;
    const q = Number(row?.netQty ?? 0);
    if (q > 0) return "BUY";
    if (q < 0) return "SELL";
    return "FLAT";
  };

  const tableRows = filteredPositionReportList || [];

  const tableTh = embedded
    ? {
        ...thSt(),
        padding: "8px 10px",
        fontSize: 10,
        letterSpacing: "0.02em",
        textTransform: "none",
        fontWeight: 600,
        borderRight: "none",
        boxShadow: "none",
        background: "transparent",
        borderBottom: "1px solid rgba(15,23,42,0.08)",
      }
    : thSt();
  const tableTd = (tint) =>
    embedded
      ? {
          padding: "7px 10px",
          fontSize: 12,
          whiteSpace: "nowrap",
          color: EMB.text,
          background: "transparent",
          borderBottom: "none",
          borderRight: "none",
        }
      : tdSt(tint);
  const tdEmbeddedClass = (tint) =>
    embedded
      ? `pr-td pr-td--${tint === "buy" ? "buy" : tint === "sell" ? "sell" : "neutral"}`
      : undefined;
  const C = darkTheme && !embedded ? C_DARK : C_LIGHT;
  const footTd = (extra = {}) =>
    embedded
      ? {
          ...tableTd("neutral"),
          fontWeight: 600,
          fontSize: 11,
          fontVariantNumeric: "tabular-nums",
          background: darkTheme ? "var(--bg-primary, #000000)" : "#f3f4f6",
          borderTop: darkTheme ? "none" : "1px solid rgba(15, 23, 42, 0.08)",
          ...extra,
        }
      : {};
  const selEmb = embedded ? selStEmbeddedShell : () => selSt(C);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      className={embedded ? "pr-root pr-root--embedded" : `pr-root fx-book-page${showMobileCards ? " pr-root--mobile-list" : ""}`}
      style={{
        padding: embedded ? "0 6px 8px" : showMobileCards ? "8px 10px" : "12px 16px",
        background: embedded ? "transparent" : C.pageBg,
        minHeight: embedded ? 0 : "100%",
        color: embedded ? EMB.text : C.tx,
        fontFamily: FONT_ROBOTO,
        ...(embedded ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 0 } : {}),
      }}
    >

      {/* ══ FILTER PANEL ══ */}
      {(!embedded || (isCloseButtonVisible && closePositonSelected.length > 0)) && (
      <>
      {!embedded && (
        <div className="fx-mobile-report-toolbar">
          <button
            type="button"
            className={`fx-mobile-report-toolbar__btn${mobileFiltersOpen ? " fx-mobile-report-toolbar__btn--active" : ""}`}
            onClick={() => setMobileFiltersOpen((o) => !o)}
          >
            Filters {mobileFiltersOpen ? "▴" : "▾"}
          </button>
          {showMobileCards && isCheckBoxVisible && tableRows.length > 0 && (
            <label className="fx-mobile-report-toolbar__select-all">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={(e) => handleCheckAll(e.target.checked)}
              />
              <span>All</span>
            </label>
          )}
          <button type="button" className="fx-mobile-report-toolbar__btn fx-mobile-report-toolbar__btn--primary" onClick={handleGetPosition} disabled={positionReportLoading}>
            {positionReportLoading ? "Loading…" : "Refresh"}
          </button>
          {showMobileCards && isCloseButtonVisible && closePositonSelected.length > 0 && (
            <button
              type="button"
              className="fx-mobile-report-toolbar__btn fx-mobile-report-toolbar__btn--danger"
              onClick={() => setShowConfirmAll(true)}
            >
              Close ({closePositonSelected.length})
            </button>
          )}
        </div>
      )}
      <div
        className={embedded ? "pr-embedded-filters" : `fx-mobile-report-filters${mobileFiltersOpen ? " fx-mobile-report-filters--open" : ""}`}
        style={{
          background: embedded ? EMB.panel : C.panelBg,
          border: embedded ? `1px solid color-mix(in srgb, ${EMB.border} 80%, transparent)` : `1px solid ${C.border}`,
          borderRadius: embedded ? 8 : 5,
          padding: embedded ? "6px 8px" : "14px 16px",
          marginBottom: embedded ? 6 : 4,
          maxHeight: "none",
          overflowY: "visible",
        }}
      >

        {embedded ? (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "8px 12px",
              justifyContent: "flex-end",
            }}
          >
            {isCloseButtonVisible && closePositonSelected.length > 0 && (
              <button
                type="button"
                onClick={() => setShowConfirmAll(true)}
                disabled={false}
                style={{
                  ...btnStCompact(C.red, false),
                  minWidth: 88,
                }}
              >
                Close ({closePositonSelected.length})
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="fx-book-filter-row" style={{ display: "flex", flexWrap: "wrap", gap: 20, alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {CR_TYPES.map(({ value, label }) => (
                  <label key={value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.tx, userSelect: "none" }}>
                    <input type="radio" name="crtype" value={value} checked={filters.crtype === value} style={{ accentColor: C.headerBg, width: 15, height: 15 }} onChange={() => setF("crtype", value)} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {DISP_TYPES.map(({ value, label }) => (
                  <label key={value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.tx, userSelect: "none" }}>
                    <input type="radio" name="disptype" value={value} checked={filters.disptype === value} style={{ accentColor: C.headerBg, width: 15, height: 15 }} onChange={() => setF("disptype", value)} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ minWidth: 200, flex: "1 1 200px", maxWidth: 300 }}>
                <Select styles={selSt(C)} isClearable isLoading={loadingSeg} placeholder={loadingSeg ? "Loading…" : "Segment"} value={segmentSelectValue} onChange={(v) => { setF("segment", v); setF("symbol", null); }} options={segmentOptions} />
              </div>
              <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                <Select styles={selSt(C)} placeholder="Select Valan" value={valanSelectValue} onChange={(v) => setF("valan", v)} options={valanOptions} />
              </div>
              {userRole !== "User" && (
                <>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {REPORT_TYPES.filter((r) => !(userRole === "Partner" && r.value === "script")).map(({ value, label }) => (
                      <label key={value} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.tx, userSelect: "none" }}>
                        <input
                          type="radio"
                          name="reporttype"
                          value={value}
                          checked={filters.reporttype === value}
                          style={{ accentColor: C.headerBg, width: 15, height: 15 }}
                          onChange={() => {
                            setF("reporttype", value);
                            setF("clientcode", null);
                            setF("symbol", null);
                            setF("subbroker", null);
                          }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {filters.reporttype === "client" && (
                    <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                      <Select styles={selSt(C)} isClearable isLoading={loadingCli} placeholder={loadingCli ? "Loading…" : "Select Client"} value={filters.clientcode} onChange={(v) => setF("clientcode", v)} options={clientOptions} />
                    </div>
                  )}
                  {filters.reporttype === "script" && (
                    <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                      <Select styles={selSt(C)} isClearable placeholder="Select Symbol" value={filters.symbol} onChange={(v) => setF("symbol", v)} options={symbolOptions} />
                    </div>
                  )}
                </>
              )}
              {userRole === "User" && (
                <div style={{ minWidth: 160, flex: "1 1 160px", maxWidth: 260 }}>
                  <input value={userName} disabled style={{ ...inpSt(C), opacity: 0.7 }} />
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
              <button onClick={handleGetPosition} disabled={positionReportLoading} style={btnSt(C.headerBg, positionReportLoading)}>
                {positionReportLoading ? "Loading…" : "Get Position"}
              </button>
              <button onClick={handleExport} disabled={!tableRows.length} style={btnSt(C.headerBg, !tableRows.length)}>
                Export
              </button>
              {isCloseButtonVisible && (
                <button onClick={() => setShowConfirmAll(true)} disabled={closePositonSelected.length === 0} style={{ ...btnSt(C.red, closePositonSelected.length === 0), minWidth: 130 }}>
                  Close Position ({closePositonSelected.length})
                </button>
              )}
            </div>
          </>
        )}
      </div>
      </>
      )}

      {/* ══ TOTALS BAR moved below table (full page) ══ */}

      {/* ══ LOADING BAR (full page — embedded refetch is quiet) ══ */}
      {positionReportLoading && !embedded && (
        <div style={{ height:3, borderRadius:2, marginBottom:4,
          background:`linear-gradient(90deg,${C.headerBg},${C.green})` }} />
      )}

      {/* ══ SEARCH BAR (full page only) ══ */}
      {!embedded && (
        <div
          className="fx-book-search-row"
          style={{
            background: C.panel2Bg,
            border: `1px solid ${C.border}`,
            borderRadius: 5,
            padding: "8px 16px",
            marginBottom: 4,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <div
            className="fx-book-search-box"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              background: C.panelBg,
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              padding: "5px 12px",
              flex: "1 1 220px",
              minWidth: 0,
            }}
          >
            <span style={{ color: C.muted, fontSize: 14 }}>🔍</span>
            <input
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                debouncedSearch(e.target.value);
              }}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: 13,
                color: C.tx,
                fontFamily: "inherit",
              }}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  debouncedSearch("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: C.muted,
                  fontSize: 11,
                }}
              >
                ✕
              </button>
            )}
          </div>
          <span style={{ marginLeft: "auto", fontSize: 12, color: C.muted }}>
            Total:{" "}
            <strong style={{ color: C.green }}>{totalRecordPositionReport.toLocaleString()}</strong>
            {closePositonSelected.length > 0 && (
              <span style={{ color: C.red, marginLeft: 6 }}>· {closePositonSelected.length} sel</span>
            )}
          </span>
        </div>
      )}

      {/* ══ POSITION LIST (mobile cards) / TABLE (desktop + embedded) ══ */}
      {showMobileCards ? (
        <div
          className="pr-mobile-list"
          onScroll={handleScroll}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {tableRows.length === 0 && !positionReportLoading ? (
            <div className="pr-mobile-list__empty">
              No positions found. Open <strong>Filters</strong>, then tap <strong>Refresh</strong>.
            </div>
          ) : (
            tableRows.map((row, i) => (
              <PositionAdvRow
                key={row.rowId || i}
                row={row}
                isCheckBoxVisible={isCheckBoxVisible}
                isCloseButtonVisible={isCloseButtonVisible}
                isAdminRole={isAdminRole}
                onCheck={handleCheck}
                onClose={(r) => {
                  dispatch(setPositionCloseConfirmDialog(true));
                  dispatch(setPositionCloseData({ ...r, marketType: "market" }));
                }}
                orderTypeForRow={orderTypeForRow}
                C={C}
              />
            ))
          )}

          {isRolloverVisible && tableRows.length > 0 && (
            <div className="pr-mobile-list__actions">
              <button
                type="button"
                onClick={handleRollover}
                disabled={closePositonSelected.length === 0}
                style={btnSt(C.headerBg, closePositonSelected.length === 0)}
              >
                Rollover
              </button>
            </div>
          )}

          {positionReportLoading && tableRows.length > 0 && (
            <div className="pr-mobile-list__loading">Loading more records…</div>
          )}
          {!positionReportLoading && tableRows.length > 0
            && tableRows.length >= totalRecordPositionReport
            && totalRecordPositionReport > 0 && (
            <div className="pr-mobile-list__done">
              ✓ All {totalRecordPositionReport.toLocaleString()} records loaded
            </div>
          )}
        </div>
      ) : (
      <div
        className={embedded ? "pr-embedded-table-wrap" : "fx-book-table-scroll"}
        onScroll={handleScroll}
        style={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: embedded ? undefined : "calc(100vh - 370px)",
          flex: embedded ? "1 1 0%" : undefined,
          minHeight: embedded ? 0 : undefined,
          border: embedded ? "1px solid var(--border, #c0b8ae)" : `1px solid ${C.border}`,
          borderRadius: embedded ? 6 : 5,
          background: embedded ? "var(--bg-primary, #fffef9)" : undefined,
        }}
      >
        <table
          className={embedded ? "pr-table pr-table--embedded" : "pr-table pr-table--page"}
          style={{ width: "100%", borderCollapse: "collapse", minWidth: embedded ? 0 : 900 }}
        >
          <thead>
            <tr>
              {/* Checkbox column header */}
              <th
                data-pr-col="select"
                style={{ ...tableTh, width: embedded ? 36 : 44, textAlign: "center", padding: embedded ? "5px 4px" : "9px 8px" }}
              >
                {isCheckBoxVisible && tableRows.length > 0 && (
                  <input
                    type="checkbox"
                    className={embedded ? "pr-checkbox" : undefined}
                    checked={allChecked}
                    onChange={(e) => handleCheckAll(e.target.checked)}
                    style={{
                      width: 15,
                      height: 15,
                      cursor: "pointer",
                    }}
                  />
                )}
              </th>

              {/* Sortable data columns */}
              {[
                ['segment',      'Segment'],
                ['script',       'Symbol'],
                ['openTime',     'Time'],
                ['type',         'Type'],
                ['lots',         'Lots'],
                ['qty',          'Qty'],
                ['openPrice',    'Price'],
                ['currentPrice', 'Price'],
                ['profit',       'P&L'],
              ].map(([id, label]) => (
                <th key={id} data-pr-col={id} style={tableTh} onClick={() => handleSort(id)}>
                  {label}{arrow(id)}
                </th>
              ))}

              {/* Authority — Admin/Master only */}
              {isAdminRole && (
                <th data-pr-col="authority" style={tableTh} onClick={() => handleSort("authorityName")}>
                  Authority{arrow("authorityName")}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {tableRows.length === 0 && !positionReportLoading ? (
              <tr>
                <td
                  colSpan={prTableColCount}
                  style={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    padding: embedded ? "32px 16px" : 50,
                    minHeight: embedded ? 140 : undefined,
                    color: embedded ? EMB.muted : C.muted,
                    fontSize: embedded ? 12 : 14,
                    background: embedded ? "var(--bg-primary, #fffef9)" : undefined,
                    borderBottom: "none",
                  }}
                >
                  {embedded ? (
                    <>No outstanding positions for these filters. Try another segment, valan, or report filters — the list updates automatically.</>
                  ) : (
                    <>
                      No positions found. Select filters and click <strong style={{ color: C.headerBg }}>Get Position</strong>.
                    </>
                  )}
                </td>
              </tr>
            ) : (
              tableRows.map((row, i) => {
                const mtmVal   = (row.profit ?? row.mtm ?? 0) * (row.currencyRate||1);
                const mtmColor = embedded
                  ? (mtmVal > 0 ? "#2563eb" : mtmVal < 0 ? "#dc2626" : EMB.text)
                  : (mtmVal>0 ? C.green : mtmVal<0 ? C.red : C.tx);
                const tint     = row.netQty>0 ? 'buy' : row.netQty<0 ? 'sell' : 'none';
                const cell     = tableTd(tint);
                const netClr   = embedded
                  ? (row.netQty > 0 ? "#2563eb" : row.netQty < 0 ? "#dc2626" : EMB.text)
                  : (row.netQty>0 ? C.green : row.netQty<0 ? C.red : C.tx);

                return (
                  <tr key={row.rowId||i}
                    className="pr-pos-row"
                    onMouseEnter={e => { if (!embedded) e.currentTarget.style.filter='brightness(0.96)'; }}
                    onMouseLeave={e => { if (!embedded) e.currentTarget.style.filter=''; }}>

                    {/* Checkbox */}
                    <td
                      className={tdEmbeddedClass(tint)}
                      data-pr-col="select"
                      style={{ ...cell, textAlign:'center', padding:'8px 6px' }}
                      onClick={e => e.stopPropagation()}
                    >
                      {isCheckBoxVisible && row.netQty !== 0 && (
                        <input type="checkbox" className={embedded ? "pr-checkbox" : undefined} checked={!!row.isCheck}
                          onChange={() => handleCheck(row)}
                          style={{ width:15, height:15, cursor:'pointer' }} />
                      )}
                    </td>

                    <td className={tdEmbeddedClass(tint)} data-pr-col="segment" style={{ ...cell, fontWeight:600 }}>{row.segment}</td>
                    <td className={tdEmbeddedClass(tint)} data-pr-col="script" style={{ ...cell, fontWeight:700 }}>{row.script}</td>
                    <td className={tdEmbeddedClass(tint)} data-pr-col="openTime" style={cell}>{formatOpenPositionTime(row.openTime)}</td>
                    <td
                      className={tdEmbeddedClass(tint)}
                      data-pr-col="type"
                      style={{
                        ...cell,
                        fontWeight: 700,
                        color: String(row.type).toLowerCase() === 'buy' ? C.green : C.red,
                      }}
                    >
                      {row.type || orderTypeForRow(row)}
                    </td>
                    <td className={tdEmbeddedClass(tint)} data-pr-col="lots" style={cell}>{row.lots}</td>
                    <td className={tdEmbeddedClass(tint)} data-pr-col="qty" style={cell}>{row.qty}</td>
                    <td className={tdEmbeddedClass(tint)} data-pr-col="openPrice" style={{ ...cell, fontFamily: FONT_ROBOTO_MONO }}>
                      {Number(row.openPrice||0).toFixed(row.digitAfterPoint||2)}
                    </td>
                    <td className={tdEmbeddedClass(tint)} data-pr-col="currentPrice" style={{ ...cell, fontFamily: FONT_ROBOTO_MONO }}>
                      {Number(row.currentPrice ?? row.ltp ?? 0).toFixed(row.digitAfterPoint||2)}
                    </td>
                    <td
                      className={tdEmbeddedClass(tint)}
                      data-pr-col="profit"
                      style={{
                        ...cell,
                        fontWeight: 700,
                        color: mtmColor,
                        position: !isAdminRole ? "relative" : undefined,
                        paddingRight: !isAdminRole && isCloseButtonVisible ? 28 : cell.paddingRight,
                      }}
                    >
                      {mtmVal.toFixed(2)}
                      {(row.currencyRate||1) > 1 && (
                        <span style={{ fontSize:10, marginLeft:4, color:C.muted }}>
                          ({row.currencySymbol}:{row.currencyRate})
                        </span>
                      )}
                      {!isAdminRole && isCloseButtonVisible && row.netQty !== 0 && (
                        <button
                          type="button"
                          className="pr-pos-row__close"
                          title="Close Position"
                          aria-label="Close position"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(setPositionCloseConfirmDialog(true));
                            dispatch(setPositionCloseData({ ...row, marketType: "market" }));
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </td>

                    {/* Authority */}
                    {isAdminRole && (
                      <td
                        className={tdEmbeddedClass(tint)}
                        data-pr-col="authority"
                        style={{
                          ...cell,
                          color: C.muted,
                          position: "relative",
                          paddingRight: isCloseButtonVisible ? 28 : cell.paddingRight,
                        }}
                      >
                        {row.authorityName}
                        {isCloseButtonVisible && row.netQty !== 0 && (
                          <button
                            type="button"
                            className="pr-pos-row__close"
                            title="Close Position"
                            aria-label="Close position"
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(setPositionCloseConfirmDialog(true));
                              dispatch(setPositionCloseData({ ...row, marketType: "market" }));
                            }}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}

            {/* ── Rollover — full page only ── */}
            {tableRows.length > 0 && isRolloverVisible && !embedded && (
              <tr>
                <td colSpan={2} style={{ padding:'10px 12px', background:C.panelBg }}>
                  <button
                    onClick={handleRollover}
                    disabled={closePositonSelected.length === 0}
                    style={btnSt(C.headerBg, closePositonSelected.length===0)}>
                    Rollover
                  </button>
                </td>
                <td colSpan={20} style={{ background:C.panelBg }} />
              </tr>
            )}
          </tbody>
          {embedded && totals && totals.colFoot && tableRows.length > 0 && (
            <tfoot>
              <tr className="pr-embedded-tfoot-row pr-embedded-tfoot-row--cols">
                <td
                  className={tdEmbeddedClass("neutral")}
                  data-pr-col="select"
                  style={{ ...footTd(), textAlign: "center" }}
                />
                <td
                  className={tdEmbeddedClass("neutral")}
                  data-pr-col="segment"
                  style={{ ...footTd(), color: EMB.muted, letterSpacing: "0.02em" }}
                >
                  Total
                </td>
                <td className={tdEmbeddedClass("neutral")} data-pr-col="script" style={footTd()} />
                <td className={tdEmbeddedClass("neutral")} data-pr-col="openTime" style={footTd()} />
                <td className={tdEmbeddedClass("neutral")} data-pr-col="type" style={footTd()} />
                <td className={tdEmbeddedClass("neutral")} data-pr-col="lots" style={footTd({ textAlign: "center" })}>
                  {Number(totals.colFoot.sumLots || 0).toFixed(2)}
                </td>
                <td className={tdEmbeddedClass("neutral")} data-pr-col="qty" style={footTd({ textAlign: "center" })}>
                  {Math.round(totals.colFoot.sumBuyQty).toLocaleString()} / {Math.round(totals.colFoot.sumSellQty).toLocaleString()}
                </td>
                <td className={tdEmbeddedClass("neutral")} data-pr-col="openPrice" style={{ ...footTd(), textAlign: "center", color: EMB.muted }}>
                  —
                </td>
                <td className={tdEmbeddedClass("neutral")} data-pr-col="currentPrice" style={{ ...footTd(), textAlign: "center", color: EMB.muted }}>
                  —
                </td>
                <td
                  className={tdEmbeddedClass("neutral")}
                  data-pr-col="profit"
                  style={{
                    ...footTd({ textAlign: "right", fontFamily: FONT_ROBOTO_MONO }),
                    textAlign: "center",
                    color: totals.tMTM > 0 ? "#2563eb" : totals.tMTM < 0 ? "#dc2626" : EMB.text,
                  }}
                >
                  {totals.tMTM.toFixed(2)}
                </td>
                {isAdminRole && (
                  <td className={tdEmbeddedClass("neutral")} data-pr-col="authority" style={footTd()} />
                )}
              </tr>
              <tr className="pr-embedded-tfoot-row pr-embedded-tfoot-row--summary">
                <td colSpan={prTableColCount} className={tdEmbeddedClass("neutral")} style={footTd()}>
                  <div className="pr-embedded-summary__inner pr-embedded-summary__inner--tfoot">
                    {[
                      ["N. B. Q", totals.tBuyQty, totals.tBuyQty > 0 ? "#2563eb" : EMB.text],
                      ["N. S. Q", totals.tSellQty, totals.tSellQty < 0 ? "#dc2626" : EMB.text],
                      ["T. N. Q", totals.tNetQty, totals.tNetQty > 0 ? "#2563eb" : totals.tNetQty < 0 ? "#dc2626" : EMB.text],
                      ["T. B", totals.tBuy, totals.tBuy > 0 ? "#2563eb" : EMB.text],
                      ["T. S", Math.abs(totals.tSell), totals.tSell < 0 ? "#dc2626" : EMB.text],
                      ["T. V", Math.abs(totals.tValue), EMB.text],
                      ["T. H", totals.tHodiyu, EMB.text],
                      ["T. R", totals.tRecords, EMB.text],
                    ].map(([label, val, color]) => (
                      <span key={label} className="pr-embedded-summary__item">
                        <span className="pr-embedded-summary__label">{label}:</span>{" "}
                        <strong style={{ color }}>{val}</strong>
                      </span>
                    ))}
                    {showBalance && (
                      <span className="pr-embedded-summary__item pr-embedded-summary__item--end">
                        <span className="pr-embedded-summary__label">L.B</span>{" "}
                        <strong
                          style={{
                            color:
                              totals.ledgerBal > 0 ? "#2563eb" : totals.ledgerBal < 0 ? "#dc2626" : EMB.text,
                          }}
                        >
                          {(totals.ledgerBal || 0).toFixed(2)}
                        </strong>
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>

        {/* Scroll loader */}
        {!embedded && positionReportLoading && tableRows.length > 0 && (
          <div style={{ textAlign:'center', padding:16, color:C.headerBg, fontSize:13 }}>
            Loading more records…
          </div>
        )}

        {/* All loaded */}
        {!embedded && !positionReportLoading && tableRows.length > 0
          && tableRows.length >= totalRecordPositionReport
          && totalRecordPositionReport > 0 && (
          <div style={{ textAlign:'center', padding:10, color:C.muted, fontSize:11 }}>
            ✓ All {totalRecordPositionReport.toLocaleString()} records loaded
          </div>
        )}
      </div>
      )}

      {totals && !embedded && (
        <div
          className="pr-page-totals"
          style={{
            background: C.panel2Bg,
            border: `1px solid ${C.border}`,
            borderRadius: 5,
            padding: "8px 10px",
            marginTop: 6,
            flexShrink: 0,
          }}
        >
          <div className="pr-page-totals__grid">
            {[
              ["N. B. Q", totals.tBuyQty, totals.tBuyQty > 0 ? C.green : C.tx],
              ["N. S. Q", totals.tSellQty, totals.tSellQty < 0 ? C.red : C.tx],
              ["T. N. Q", totals.tNetQty, totals.tNetQty > 0 ? C.green : totals.tNetQty < 0 ? C.red : C.tx],
              ["T. B", totals.tBuy, totals.tBuy > 0 ? C.green : C.tx],
              ["T. S", Math.abs(totals.tSell), totals.tSell < 0 ? C.red : C.tx],
              ["T. V", Math.abs(totals.tValue), C.green],
              ["T. H", totals.tHodiyu, C.green],
              ["T. R", totals.tRecords, C.accent],
            ].map(([label, val, color]) => (
              <div key={label} className="pr-page-totals__cell">
                <span className="pr-page-totals__label">{label}</span>
                <strong className="pr-page-totals__value" style={{ color }}>{val}</strong>
              </div>
            ))}
          </div>
          <div className="pr-page-totals__footer">
            {showBalance && (
              <div className="pr-page-totals__cell pr-page-totals__cell--wide">
                <span className="pr-page-totals__label">L. B</span>
                <strong
                  className="pr-page-totals__value"
                  style={{ color: totals.ledgerBal > 0 ? C.green : totals.ledgerBal < 0 ? C.red : C.tx }}
                >
                  {(totals.ledgerBal || 0).toFixed(2)}
                </strong>
              </div>
            )}
            <div className="pr-page-totals__cell">
              <span className="pr-page-totals__label">T. MTM</span>
              <strong
                className="pr-page-totals__value"
                style={{ color: totals.tMTM > 0 ? C.green : totals.tMTM < 0 ? C.red : C.tx }}
              >
                {totals.tMTM.toFixed(2)}
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* ══ SINGLE CLOSE POSITION MODAL ══ */}
      {positionCloseConfirmDialog && positionCloseData && positionCloseData.rowId && (
        <ClosePositionModal
          row={positionCloseData}
          loading={isPositionCloseLoading}
          darkTheme={darkTheme}
          onClose={() => dispatch(setPositionCloseConfirmDialog(false))}
          onConfirm={handleSingleClose}
        />
      )}

      {/* ══ BULK CLOSE CONFIRM MODAL ══ */}
      {showConfirmAll && (
        <ConfirmAllModal
          count={closePositonSelected.length}
          loading={isPositionCloseLoading}
          darkTheme={darkTheme}
          onClose={() => setShowConfirmAll(false)}
          onConfirm={handleCloseAll}
        />
      )}
    </div>
  );
}
